import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'

const CONDENSED = "'Fira Sans Condensed', 'Roboto Condensed', Roboto, sans-serif"

const useStyles = makeStyles({
  root: { position: 'relative' },
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    height: '22px',
    padding: '0 8px',
    borderRadius: '6px',
    border: '1px solid #2a2d33',
    background: '#1b1d21',
    color: '#e6e8ea',
    fontSize: '12px',
    cursor: 'pointer',
    '&:hover:enabled': { borderColor: '#3a3e46', background: '#24272d' },
    '&:disabled': { opacity: 0.5, cursor: 'default' },
  },
  // Opens upward: the control bar sits at the bottom of the stage, so a menu dropping down
  // would fall off the viewport.
  menu: {
    position: 'absolute',
    left: 0,
    bottom: '30px',
    width: '176px',
    padding: '6px',
    borderRadius: '12px',
    background: '#1b1d21',
    border: '1px solid #3a3e46',
    boxShadow: '0 -10px 30px rgba(0, 0, 0, 0.5)',
    zIndex: 20,
  },
  menuLabel: {
    padding: '6px 8px 8px',
    fontFamily: CONDENSED,
    fontSize: '11px',
    fontWeight: 500,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: '#6f757e',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    width: '100%',
    padding: '8px',
    border: 'none',
    borderRadius: '8px',
    background: 'transparent',
    color: '#e6e8ea',
    fontSize: '13px',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': { background: '#2a2d33' },
  },
  optionCurrent: { background: '#24272d' },
})

const Chevron = () => (
  <svg
    width='11'
    height='11'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.6'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M6 15l6-6 6 6' />
  </svg>
)

const ScoreboardSourcePicker = ({ source, options, onChange, disabled }) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDocument = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocument)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocument)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const label = options.find((option) => option.value === source)?.label ?? source

  return (
    <div className={classes.root} ref={rootRef}>
      <button
        type='button'
        className={classes.trigger}
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup='listbox'
        aria-expanded={open}
        aria-label={`Scoreboard source: ${label}`}
      >
        <span>{label}</span>
        <Chevron />
      </button>
      {open && (
        <div className={classes.menu} role='listbox'>
          <div className={classes.menuLabel}>Source</div>
          {options.map((option) => (
            <button
              key={option.value}
              type='button'
              role='option'
              aria-selected={option.value === source}
              className={`${classes.option} ${
                option.value === source ? classes.optionCurrent : ''
              }`}
              onClick={() => {
                setOpen(false)
                if (option.value !== source) onChange(option.value)
              }}
            >
              <span style={{ flex: 1 }}>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

ScoreboardSourcePicker.propTypes = {
  source: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ value: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

ScoreboardSourcePicker.defaultProps = { disabled: false }

export default ScoreboardSourcePicker
